<template>
	<v-dialog v-model='display' :width='dialogWidth'>
		<template v-slot:activator='{ on }'>
			<v-text-field
				v-bind='textFieldProps'
				:disabled='disabled'
				:loading='loading'
				:label='label'
				:value='formattedDatetime'
				v-on='on'
				readonly
			>
				<template v-slot:progress>
					<slot name='progress'>
						<v-progress-linear color='primary' indeterminate absolute height='2'></v-progress-linear>
					</slot>
				</template>
			</v-text-field>
		</template>

		<v-card class='py-4 date-time-picker-container'>
			<v-card-text class='px-0 py-0'>
				<v-tabs fixed-tabs class="date-time-control-tabs mb-6" v-model='activeTab'>

					<v-tab key='calendar'>
						<slot name='dateIcon'>
							<span>date</span>
						</slot>
					</v-tab>

					<v-tab key='timer' :disabled='dateSelected'>
						<slot name='timeIcon'>
							<span>time</span>
						</slot>
					</v-tab>

					<v-divider class='mb-4 d-block'></v-divider>

					<v-tab-item key='calendar'>
						<v-date-picker
							v-model='date'
							v-bind='datePickerProps'
							@change="setItems( normalizeDateTime(type) )"
							full-width
						></v-date-picker>
					</v-tab-item>

					<v-tab-item key='timer'>
						<v-time-picker
							ref='timer'
							class='v-time-picker-custom'
							v-model='time'
							v-bind='timePickerProps'
							@change="setItems( normalizeDateTime(type) )"
							full-width
						></v-time-picker>
					</v-tab-item>

				</v-tabs>
			</v-card-text>

			<v-card-actions>
				<slot name='actions' :parent='this'>
					<v-alert
						v-if="showError"
						type="warning"
					> {{error}}. </v-alert>
				</slot>
			</v-card-actions>

			<v-card-actions>
				<v-spacer></v-spacer>
				<slot name='actions' :parent='this'>
					<v-btn
						color='grey lighten-1'
						text
						@click.native='clearHandler'
					>{{ clearText }}</v-btn>

					<v-btn
						:color='datePickerColor'
						:disabled='showError'
						text
						@click='okHandler'
					>{{ okText }}</v-btn>
				</slot>
			</v-card-actions>

		</v-card>
	</v-dialog>
</template>

<script>
import { format, parse } from 'date-fns'
import { mapGetters } from 'vuex'

const DEFAULT_DATE = ''
const DEFAULT_TIME = '00:00'
const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd'
const DEFAULT_TIME_FORMAT = 'HH:mm'
const DEFAULT_DIALOG_WIDTH = 340
const DEFAULT_CLEAR_TEXT = 'CLEAR'
const DEFAULT_OK_TEXT = 'OK'

export default {
	props: {
		datetime: {
			type: [Date, String],
			default: null,
		},
		type: {
			type: [String],
			default: 'from',
		},
		setItems: {
			type: [Function],
			default: () => {}
		},
		closeAlert: {
			type: [Function],
			default: () => {}
		},
		error: {
			type: String,
			default: ''
		},
		showError: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
		},
		loading: {
			type: Boolean,
		},
		label: {
			type: String,
			default: '',
		},
		dialogWidth: {
			type: String,
			default: DEFAULT_DIALOG_WIDTH,
		},
		dateFormat: {
			type: String,
			default: DEFAULT_DATE_FORMAT,
		},
		timeFormat: {
			type: String,
			default: 'HH:mm',
		},
		clearText: {
			type: String,
			default: DEFAULT_CLEAR_TEXT,
		},
		okText: {
			type: String,
			default: DEFAULT_OK_TEXT,
		},
		textFieldProps: {
			type: Object,
		},
		datePickerProps: {
			type: Object,
		},
		timePickerProps: {
			type: Object,
		},
	},
	data() {
		return {
			display: false,
			activeTab: 0,
			date: DEFAULT_DATE,
			time: DEFAULT_TIME
		}
	},
	mounted() {
		this.init()
	},
	computed: {
		...mapGetters('settings', ['styles']),

		datePickerColor() {
			const {
				navigation_bar_tint: navigationBarTint,
			} = this.styles || {}

			return navigationBarTint + ' !important'
		},
		dateTimeFormat() {
			return this.dateFormat + ' ' + this.timeFormat
		},
		defaultDateTimeFormat() {
			return DEFAULT_DATE_FORMAT + ' ' + DEFAULT_TIME_FORMAT
		},
		formattedDatetime() {
			return this.selectedDatetime ? format(this.selectedDatetime, this.dateTimeFormat) : ''
		},
		selectedDatetime() {
			if (this.date && this.time) {
				let datetimeString = this.date + ' ' + this.time

				return parse(datetimeString, this.defaultDateTimeFormat, new Date())
			} else {
				return null
			}
		},
		dateSelected() {
			return !this.date
		}
	},
	methods: {
		init() {
			if (!this.datetime) {
				return
			}

			let initDateTime
			if (this.datetime instanceof Date) {
				initDateTime = this.datetime
			} else if (typeof this.datetime === 'string' || this.datetime instanceof String) {
				initDateTime = parse(this.datetime, this.dateTimeFormat, new Date())
			}

			this.date = format(initDateTime, DEFAULT_DATE_FORMAT)
			this.time = format(initDateTime, DEFAULT_TIME_FORMAT)
		},
		okHandler() {
			this.resetPicker()
			this.$emit('input', this.selectedDatetime)
		},
		clearHandler() {
			this.resetPicker()
			this.date = DEFAULT_DATE
			this.time = DEFAULT_TIME
			this.$emit('input', null)

			this.closeAlert()
		},
		resetPicker() {
			this.display = false
			this.activeTab = 0
			if (this.$refs.timer) {
				this.$refs.timer.selectingHour = true
			}
		},
		normalizeDateTime(type) {
			return {
				type: type,
				date: this.date,
				time: this.time
			}
		}
	},
	watch: {
		datetime: function() {
			this.init()
		},
	},
}
</script>

<style>
.date-time-control-tabs div[role=tablist]{
	margin-bottom: 8px;
}
.v-time-picker-clock__ampm {
	justify-content: space-around;
}
.v-card.date-time-picker-container {
	border-radius: 13px;
}
</style>
